import { useEffect, useRef, useState } from 'react';

const SLOP = 8;
const HINT = 36;

/**
 * How far a finger has to travel before a swipe counts.
 * Short flicks still count; a small nudge does not.
 */
export function swipeThreshold(width) {
  const safe = Number.isFinite(width) && width > 0 ? width : 320;
  return Math.min(84, Math.max(48, safe * 0.18));
}

export function swipeDirection(dx, dy, allowVertical = true) {
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  if (!allowVertical) return null;
  return dy >= 0 ? 'down' : 'up';
}

/**
 * Split a drag into card movement and inner scrolling.
 * Vertical movement scrolls the text first. Whatever is left over, once the
 * text is at an edge, moves the card so up and down can still answer.
 */
export function resolveDrag({ dx, dy, axis, scrollTop = 0, scrollMax = 0, allowVertical = true }) {
  if (axis === 'y' && scrollMax > 2) {
    const next = Math.min(scrollMax, Math.max(0, scrollTop - dy));
    const consumed = scrollTop - next;
    return { x: dx * 0.12, y: dy - consumed, scrollTop: next };
  }
  if (axis === 'x') return { x: dx, y: (allowVertical ? 0.22 : 0.08) * dy, scrollTop };
  if (!allowVertical) return { x: dx, y: dy * 0.12, scrollTop };
  return { x: dx, y: dy, scrollTop };
}

export function shouldCommit(dx, dy, dt, width) {
  const dist = Math.hypot(dx, dy);
  const threshold = swipeThreshold(width);
  const velocity = dist / Math.max(1, dt);
  return dist > threshold || (dist > 28 && velocity > 0.45);
}

function interactiveChild(event) {
  const node = event.target.closest?.('a, button, input, textarea, select, label');
  return node && node !== event.currentTarget ? node : null;
}

function detachGesture(gesture) {
  if (!gesture) return;
  if (gesture.raf) cancelAnimationFrame(gesture.raf);
  window.removeEventListener('pointermove', gesture.move, true);
  window.removeEventListener('pointerup', gesture.up, true);
  window.removeEventListener('pointercancel', gesture.up, true);
  document.removeEventListener('touchmove', gesture.blockScroll, { capture: true });
}

/**
 * Pointer swipe for touch, pen, and mouse.
 * Tracks the pointer on the window so the gesture continues when a finger
 * leaves the card, and claims touch scrolling only while this surface owns it.
 */
export function useSwipe({ enabled, allowVertical = true, onSwipe, onTap, scrollRef } = {}) {
  const start = useRef(null);
  const dragRef = useRef({ x: 0, y: 0, active: false });
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });
  const opts = useRef({ enabled, allowVertical, onSwipe, onTap, scrollRef });

  useEffect(() => {
    opts.current = { enabled, allowVertical, onSwipe, onTap, scrollRef };
  }, [enabled, allowVertical, onSwipe, onTap, scrollRef]);

  useEffect(() => () => {
    detachGesture(start.current);
    start.current = null;
  }, []);

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    if (interactiveChild(event)) return;
    if (start.current && start.current.id !== event.pointerId) {
      detachGesture(start.current);
      start.current = null;
      dragRef.current = { x: 0, y: 0, active: false };
      setDrag(dragRef.current);
    }
    if (start.current) return;

    const pointerId = event.pointerId;
    const el = event.currentTarget;
    const scrollEl = opts.current.scrollRef?.current ?? null;
    const gesture = {
      x: event.clientX,
      y: event.clientY,
      t: performance.now(),
      id: pointerId,
      axis: null,
      scrollTop: scrollEl?.scrollTop ?? 0,
      moved: false,
      rawX: 0,
      rawY: 0,
      el,
      raf: 0,
    };

    const blockScroll = (touchEvent) => {
      const current = start.current;
      if (!current || current.id !== pointerId) return;
      const { allowVertical: vertical, scrollRef: scroller } = opts.current;
      const ownsVertical = vertical || !!scroller?.current;
      if (!ownsVertical && current.axis !== 'x') return;
      if (touchEvent.cancelable) touchEvent.preventDefault();
    };

    const paint = () => {
      gesture.raf = 0;
      if (start.current !== gesture) return;
      setDrag(dragRef.current);
    };

    const move = (ev) => {
      if (ev.pointerId !== pointerId || start.current !== gesture) return;
      const { enabled: isEnabled, allowVertical: vertical, scrollRef: scroller } = opts.current;
      const dx = ev.clientX - gesture.x;
      const dy = ev.clientY - gesture.y;
      gesture.rawX = dx;
      gesture.rawY = dy;
      if (!gesture.moved) {
        if (Math.hypot(dx, dy) <= SLOP) return;
        gesture.moved = true;
        gesture.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      } else if (gesture.axis === 'y' && Math.abs(dx) > Math.abs(dy) * 1.4 && Math.abs(dx) > 24) {
        gesture.axis = 'x';
        const node = scroller?.current;
        if (node) node.scrollTop = gesture.scrollTop;
      }
      if (!isEnabled) return;
      const node = scroller?.current;
      const scrollMax = node ? Math.max(0, node.scrollHeight - node.clientHeight) : 0;
      const next = resolveDrag({
        dx,
        dy,
        axis: gesture.axis,
        scrollTop: gesture.scrollTop,
        scrollMax,
        allowVertical: vertical,
      });
      if (node && next.scrollTop !== node.scrollTop) node.scrollTop = next.scrollTop;
      dragRef.current = { x: next.x, y: next.y, active: true };
      if (!gesture.raf) gesture.raf = requestAnimationFrame(paint);
    };

    const finish = (ev, cancelled) => {
      if (start.current !== gesture) return;
      start.current = null;
      detachGesture(gesture);
      const { enabled: isEnabled, allowVertical: vertical, onSwipe: swipe, onTap: tap } = opts.current;
      const visual = dragRef.current;
      const dt = Math.max(1, performance.now() - gesture.t);
      dragRef.current = { x: 0, y: 0, active: false };
      setDrag(dragRef.current);
      if (!gesture.moved) {
        if (!cancelled) tap?.(ev);
        return;
      }
      const stopClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        window.removeEventListener('click', stopClick, true);
      };
      window.addEventListener('click', stopClick, true);
      window.setTimeout(() => window.removeEventListener('click', stopClick, true), 700);
      if (!isEnabled) return;
      const width = gesture.el?.clientWidth || ev?.currentTarget?.clientWidth;
      const commitX = gesture.axis === 'x' ? gesture.rawX : 0;
      const commitY = gesture.axis === 'y' ? visual.y : 0;
      if (!shouldCommit(commitX, commitY, dt, width)) return;
      const committed = gesture.axis === 'x' ? (gesture.rawX >= 0 ? 'right' : 'left') : swipeDirection(0, visual.y, vertical);
      if (committed) swipe?.(committed);
    };

    const up = (ev) => {
      if (ev.pointerId !== pointerId) return;
      finish(ev, ev.type === 'pointercancel');
    };

    gesture.move = move;
    gesture.up = up;
    gesture.blockScroll = blockScroll;
    start.current = gesture;

    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    if (opts.current.enabled) {
      document.addEventListener('touchmove', blockScroll, { passive: false, capture: true });
    }
  };

  const pending =
    drag.active && Math.hypot(drag.x, drag.y) > HINT ? swipeDirection(drag.x, drag.y, allowVertical) : null;

  return { drag, pending, handlers: { onPointerDown } };
}
